import { z } from "zod";

/** Bump when the SaveDocument envelope changes shape. Gameplay payload has its own version (engine). */
export const SAVE_SCHEMA_VERSION = 1;

/**
 * Wallet: server-clamped currencies. Keys are currency ids from data (currencies.csv).
 * The client may display and optimistically credit these, but the server's copy always wins.
 */
export const WalletSchema = z.record(z.string(), z.number().int().nonnegative());
export type Wallet = z.infer<typeof WalletSchema>;

/**
 * The cloud save envelope.
 *  - `gameplay` is an opaque JSON payload owned by the engine (client-authoritative).
 *  - `wallet` is server-authoritative once the server has initialised it.
 *  - `revision` implements optimistic concurrency: PUT must carry the revision it last saw.
 */
export const SaveDocumentSchema = z.object({
  schemaVersion: z.literal(SAVE_SCHEMA_VERSION),
  playerId: z.string().min(1),
  revision: z.number().int().nonnegative(),
  updatedAt: z.string().datetime(),
  clientBuild: z.string().optional(),
  /** Random per-install id. Lets a client tell its own writes from another device's on conflict. */
  deviceId: z.string().optional(),
  dataVersion: z.string().optional(),
  gameplay: z.record(z.string(), z.unknown()),
  wallet: WalletSchema,
});
export type SaveDocument = z.infer<typeof SaveDocumentSchema>;

/** Body of PUT /v1/save. */
export const PutSaveRequestSchema = z.object({
  baseRevision: z.number().int().nonnegative(),
  document: SaveDocumentSchema,
});
export type PutSaveRequest = z.infer<typeof PutSaveRequestSchema>;

/** GET /v1/save returns `{ document: null }` (200) when the player has no save yet. */
export const GetSaveResponseSchema = z.object({
  document: SaveDocumentSchema.nullable(),
});
export type GetSaveResponse = z.infer<typeof GetSaveResponseSchema>;

export const PutSaveResponseSchema = z.object({
  document: SaveDocumentSchema,
});
export type PutSaveResponse = z.infer<typeof PutSaveResponseSchema>;

/** Returned with HTTP 409 when the server's revision moved past `baseRevision`. */
export const SaveConflictResponseSchema = z.object({
  conflict: z.literal(true),
  document: SaveDocumentSchema,
});
export type SaveConflictResponse = z.infer<typeof SaveConflictResponseSchema>;

export function createEmptySave(playerId: string, now: Date = new Date()): SaveDocument {
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    playerId,
    revision: 0,
    updatedAt: now.toISOString(),
    gameplay: {},
    wallet: {},
  };
}
